import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import dayjs from "../../../utils/dayjsConfig";

import { theme } from "../../../utils/global-styles/theme";

import ThemeOne from "../../shared/CourseIcons/ThemeOne";
import ThemeTwo from "../../shared/CourseIcons/ThemeTwo";
import ThemeThree from "../../shared/CourseIcons/ThemeThree";
import ThemeFour from "../../shared/CourseIcons/ThemeFour";
import ThemeFive from "../../shared/CourseIcons/ThemeFive";
import ThemeSix from "../../shared/CourseIcons/ThemeSix";
import CheckMarkIcon from "../../CourseCard/Icons/CheckMarkIcon";

const StickyHeader = ({
  hubspotClassName,
  courseType,
  title,
  provider,
  providerUrl,
  courseTheme,
  themeNumber,
  ctaText,
  ctaUrl,
  courseCost,
  courseDuration,
  courseAvailability,
  onEnrollmentClick,
  enrollmentStatus,
  courseStartDate,
  onGoToCourseClick,
  elementId,
  isAcceleratorDetail,
  fullDeviceWidth = false,
}) => {
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0),
    [scrollPosition, setScrollPosition] = useState(0),
    [stickyHeaderAnimation, setStickyHeaderAnimation] = useState("animateDown"),
    [hasCourseStarted, setHasCourseStarted] = useState(false);

  useEffect(() => {
    if (enrollmentStatus === "enrolled" && courseStartDate) {
      const currentDate = dayjs(new Date()).format("YYYY-MM-DD"),
        startDate = dayjs(courseStartDate);

      setHasCourseStarted(Number(startDate.diff(currentDate, "days")) <= 0);
    }

    function handleScroll() {
      const originalHeaderBottomPos =
        document.getElementById(elementId || "header").getBoundingClientRect()
          .bottom + window.pageYOffset;

      if (
        document.body.getBoundingClientRect().top < -originalHeaderBottomPos
      ) {
        setStickyHeaderAnimation(
          document.body.getBoundingClientRect().top > scrollPosition
            ? "animateUp"
            : "animateDown"
        );
        setStickyHeaderHeight("auto");
      } else {
        setStickyHeaderAnimation(
          document.body.getBoundingClientRect().top > scrollPosition
            ? "animateUp"
            : "animateDown"
        );
        setStickyHeaderHeight(0);
        setScrollPosition(document.body.getBoundingClientRect().top);
      }
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasCourseStarted, enrollmentStatus, courseStartDate, scrollPosition]);

  const determineIcon = (themeNumber) => {
      switch (themeNumber) {
        case 1:
          return (
            <ThemeOne
              iconColorClassName="path-primary-fill"
              width="30px"
              height="30px"
            />
          );
        case 2:
          return (
            <ThemeTwo
              iconColorClassName="path-primary-fill"
              width="30px"
              height="30px"
            />
          );
        case 3:
          return (
            <ThemeThree
              iconColorClassName="path-primary-fill"
              width="30px"
              height="30px"
            />
          );
        case 4:
          return (
            <ThemeFour
              iconColorClassName="path-primary-fill"
              width="30px"
              height="30px"
            />
          );
        case 5:
          return (
            <ThemeFive
              iconColorClassName="path-primary-fill"
              width="30px"
              height="30px"
            />
          );
        case 6:
          return (
            <ThemeSix
              iconColorClassName="path-primary-fill"
              width="30px"
              height="30px"
            />
          );
        case undefined:
          return (
            <ThemeSix
              iconColorClassName="path-primary-fill"
              width="30px"
              height="30px"
            />
          );
        default:
          console.warn(
            `"${courseTheme}" is an invalid courseTheme passed into CourseDetailHeader.js. Check valid courseTypes in with the function determineIcon() in CourseDetailHeader.js.`
          );
          return (
            <ThemeSix
              iconColorClassName="path-primary-fill"
              width="25px"
              height="25px"
            />
          );
      }
    },
    determineCtaContainerContent = () => {
      if (enrollmentStatus === "enrolled") {
        return hasCourseStarted ||
          courseAvailability.toLowerCase().trim() === "on demand" ? (
          <CtaButton
            className="main black-button"
            onClick={() => onGoToCourseClick(provider, providerUrl)}
          >
            Go to Course
          </CtaButton>
        ) : (
          <>
            <CtaButton className="main accent-bg">
              <CheckMarkIcon height="24px" width="24px" marginBottom="4px" />
              &nbsp;Enrolled
            </CtaButton>
            {courseStartDate && (
              <StartDateText>Course starts {courseStartDate}</StartDateText>
            )}
          </>
        );
      } else {
        return isAcceleratorDetail ? (
          ctaUrl ? (
            <ApplyNowButton
              className="main primary-bg secondary-background-hover"
              href={ctaUrl || null}
              target="_blank"
            >
              {ctaText || "Apply Now"}
            </ApplyNowButton>
          ) : (
            ""
          )
        ) : (
          <button
            className={`${hubspotClassName} main black-button`}
            onClick={() => onEnrollmentClick()}
          >
            {ctaText}
          </button>
        );
      }
    };

  const StickyHeaderGrid = styled.div`
      height: ${stickyHeaderHeight};
      width: 100%;
      max-width: ${fullDeviceWidth ? "" : theme.breakpoints.maxContent};
      display: grid;
      grid-template-rows: repeat(2, 1fr);
      grid-template-columns: 224px 57px 1fr 220px 224px;
      font-family: ${theme.fontFaces.headers};
      background-color: ${theme.colors.background};
      position: fixed;
      top: 0;
      z-index: 7;
      overflow-y: hidden;
      border-bottom: 2px solid ${theme.colors.accent};
      box-shadow: 0 2px 29px 0 rgba(0, 0, 0, 0.5);

      @media only screen and (max-width: ${theme.breakpoints.desktopUp}) {
        grid-template-columns: 120px 57px 1fr 220px 120px;
      }

      @media only screen and (max-width: ${theme.breakpoints.helper1100}) {
        grid-template-columns: 60px 57px 1fr 220px 60px;
        grid-template-rows: 1fr;
      }

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        grid-template-columns: 45px 1fr 134px;
        grid-template-rows: 1fr;
        padding: 0 15px;
      }

      animation: ${stickyHeaderAnimation} 2s ease-in-out;
    `,
    CourseIconContainer = styled.div`
      grid-area: 1 / 2 / span 1 / span 1;
      padding-top: 21px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        grid-area: 1 / 1 / span 1 / span 1;
      }
    `,
    CourseTitleContainer = styled.div`
      grid-area: 1 / 3 / span 1 / span 1;
      padding: 20px 0;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        grid-area: 1 / 2 / span 1 / span 1;
        padding-right: 10px;
      }
    `,
    CourseTitle = styled.h2`
      font-size: 24px;
      font-weight: 500;
      letter-spacing: -0.22px;
      line-height: 30px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        font-size: 20px;
        font-weight: 500;
        letter-spacing: -0.18px;
        line-height: 24px;
      }
    `,
    CourseInfoContainer = styled.div`
      grid-area: 2 / 3 / span 1 / span 1;
      display: flex;
      padding-bottom: 30px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        display: none;
      }
    `,
    CourseInfoText = styled.div`
      font-size: 0.9em;
      font-weight: bold;
      line-height: 1.25em;
      border-top: 4px solid ${theme.colors.main};
      margin-right: 1.5em;
      padding-top: 5px;
      text-transform: capitalize;
    `,
    CtaButtonContainer = styled.div`
      grid-area: 1 / 4 / span 2 / span 1;
      display: flex;
      flex-direction: column;
      justify-content: center;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        padding-top: 20px;
        justify-content: flex-start;
        grid-area: 1 / 3 / span 1 / span 1;
        padding-bottom: 10px;
      }
    `,
    CtaButton = styled.div``,
    ApplyNowButton = styled.a`
      height: 56px;
      width: 218px;
      font-size: 24px;
      font-weight: bold;
      letter-spacing: 0.68px;
      line-height: 15px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        height: 42px;
        width: 109px;
        font-size: 12px;
        font-weight: bold;
        letter-spacing: 0.34px;
        line-height: 15px;
      }
    `,
    StartDateText = styled.p`
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.37px;
      line-height: 16px;
      text-align: center;
      font-family: ${theme.fontFaces.headers};
      margin-top: 7px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        display: none;
      }
    `;

  return (
    <StickyHeaderGrid id="sticky-header">
      <CourseIconContainer>{determineIcon(themeNumber)}</CourseIconContainer>
      <CourseTitleContainer>
        <CourseTitle className="main">{title}</CourseTitle>
      </CourseTitleContainer>
      <CourseInfoContainer>
        <CourseInfoText>{courseCost}</CourseInfoText>
        <CourseInfoText>
          {isAcceleratorDetail
            ? dayjs(courseStartDate).format("MMM D, YYYY")
            : courseStartDate}
        </CourseInfoText>
        <CourseInfoText>{courseDuration}</CourseInfoText>
      </CourseInfoContainer>
      <CtaButtonContainer>{determineCtaContainerContent()}</CtaButtonContainer>
    </StickyHeaderGrid>
  );
};

StickyHeader.propTypes = {
  courseType: PropTypes.string,
  title: PropTypes.string,
  provider: PropTypes.string,
  providerUrl: PropTypes.string,
  courseTheme: PropTypes.string,
  courseCost: PropTypes.string,
  courseAvailability: PropTypes.string,
  courseDuration: PropTypes.string,
  ctaText: PropTypes.string,
  ctaUrl: PropTypes.string,
  onEnrollmentClick: PropTypes.func,
  enrollmentStatus: PropTypes.string,
  courseStartDate: PropTypes.string,
  elementId: PropTypes.string,
  isAcceleratorDetail: PropTypes.string,
};

export default StickyHeader;
